/**
 * A ceremony batch's numbers (spec 2026-10-02 upacara bersama, 3.3): they run on from the
 * starting number in row order; a page holds the parish's rows-per-page (`halamanPenuh`), so
 * the batch fills the starting page and turns to the next. Every page is read as the book holds
 * it (`isiHalaman`): a page turned to is filled from its own count, never assumed empty, and a
 * page already full is passed over, so no page ever holds more rows than the book has. A book
 * without page numbers keeps none. A number already in the book stops the batch at the preview
 * (nothing is saved until the starting number changes); a gap after the book's last number only
 * warns, in Mode Salin Buku's words (`periksaNomor`).
 */
import { halamanPenuh, periksaNomor } from '../registers'

export interface TempatEntri {
  buku: string
  halaman: number | null
  nomor: number
}

export interface KonteksNomor {
  barisPerHalaman: number
  /** The entries already on each page of this book; a page it does not name holds none. */
  isiHalaman: ReadonlyMap<number, number>
  /** Every number already in this book. */
  terpakai: readonly number[]
  /** The book's highest number; null for an empty book. */
  terakhir: number | null
}

/** Named apart from `periksaNomor`'s `HasilNomor` (../registers), which `@satu/shared` exports. */
export interface HasilAturNomor {
  tempat: TempatEntri[]
  /** The batch's numbers already in the book: the batch stops. */
  ganda: number[]
  /** A gap after the book's last number, in Mode Salin Buku's words; null without one. */
  lompat: string | null
}

const isiDi = (k: KonteksNomor, halaman: number): number => k.isiHalaman.get(halaman) ?? 0

/** The first page from `halaman` on with room for one more entry (a full one turns, as often as needed). */
export function halamanLega(k: KonteksNomor, halaman: number): number {
  let h = halaman
  while (halamanPenuh(isiDi(k, h), k.barisPerHalaman)) h += 1
  return h
}

export function aturNomor(awal: TempatEntri, k: KonteksNomor, jumlah: number): HasilAturNomor {
  if (jumlah === 0) return { tempat: [], ganda: [], lompat: null }
  const tempat: TempatEntri[] = []
  let halaman = awal.halaman === null ? null : halamanLega(k, awal.halaman)
  let diHalaman = halaman === null ? 0 : isiDi(k, halaman)
  for (let i = 0; i < jumlah; i += 1) {
    if (halaman !== null && halamanPenuh(diHalaman, k.barisPerHalaman)) {
      halaman = halamanLega(k, halaman + 1)
      diHalaman = isiDi(k, halaman)
    }
    tempat.push({ buku: awal.buku, halaman, nomor: awal.nomor + i })
    diHalaman += 1
  }
  const terpakai = new Set(k.terpakai)
  // Only the gap: the duplicates are every number of the batch, not the first alone.
  const cek = periksaNomor({
    nomor: awal.nomor,
    buku: awal.buku,
    terpakai: new Set(),
    terakhir: k.terakhir,
  })
  return {
    tempat,
    ganda: tempat.map((t) => t.nomor).filter((n) => terpakai.has(n)),
    lompat: cek.jenis === 'melompat' ? cek.pesan : null,
  }
}
